import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  INTERACTIVE_LIMITS,
  tInteractiveOutbound,
} from '../../../../../../Chat.types';
import { checkCtaUrlSafety } from '../../InteractiveAction.functions';
import UTF16Counter from '../UTF16Counter/UTF16Counter.component';

// CTA URL: single button with display text + url. URL is validated against
// the same safety rules the backend applies (https only, no userinfo, no
// localhost/RFC-1918/metadata). Seller sees the failure inline rather than
// round-tripping to the server.
export default function CTAUrlBuilder() {
  const { t } = useTranslation(['chat/chat']);
  const { values, handleChange, touched } =
    useFormikContext<tInteractiveOutbound>();
  if (values.type !== 'cta_url') return null;

  const url = values.action.url;
  // Memoize the safety check. `checkCtaUrlSafety` does a try/catch around
  // `new URL()` plus regex + Set lookups — cheap individually, but Formik
  // re-renders the whole form subtree on every keystroke in any field, so
  // memoizing on `url` avoids re-parsing when the seller is typing in the
  // display-text or body field.
  const safety = useMemo(
    () => (url ? checkCtaUrlSafety(url) : null),
    [url],
  );
  const showUrlError = touched.action && safety && !safety.ok;

  return (
    <div className="InteractiveAction__Builder">
      <p className="InteractiveAction__Builder__Title">
        {t('interactive.ctaUrl.title')}
      </p>

      <div className="InteractiveAction__Field">
        <label className="InteractiveAction__Field__Label">
          {t('interactive.ctaUrl.displayTextLabel')}
        </label>
        <input
          type="text"
          dir="auto"
          name="action.display_text"
          // Hard-stop at Meta's UTF-16 limit so typing + paste are both
          // capped. The counter below stays in sync.
          maxLength={INTERACTIVE_LIMITS.CTA_DISPLAY_TEXT}
          className="InteractiveAction__Field__Input"
          placeholder={t('interactive.ctaUrl.displayTextPlaceholder')}
          value={values.action.display_text}
          onChange={handleChange}
        />
        <UTF16Counter
          value={values.action.display_text}
          max={INTERACTIVE_LIMITS.CTA_DISPLAY_TEXT}
        />
      </div>

      <div className="InteractiveAction__Field">
        <label className="InteractiveAction__Field__Label">
          {t('interactive.ctaUrl.urlLabel')}
        </label>
        <input
          type="url"
          name="action.url"
          maxLength={INTERACTIVE_LIMITS.CTA_URL}
          className={`InteractiveAction__Field__Input${
            showUrlError ? ' InteractiveAction__Field__Input--error' : ''
          }`}
          placeholder={t('interactive.ctaUrl.urlPlaceholder')}
          value={url}
          onChange={handleChange}
        />
        {showUrlError ? (
          <p className="InteractiveAction__Field__Error">
            {t(`interactive.ctaUrl.urlError.${safety.reason}`)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
